import * as apiGetDetails from 'api/shoppingCart/GetShoppingCartGetDetailsIdV1';
import { Data as ReceiptData } from 'components/desktop/ShoppingCart/Detail/misc/Receipt';
import { Ingredient } from 'types/ShoppingCart/Ingredient';
import { Source } from 'types/ShoppingCart/Source';
import { Translation } from 'types/_general/Translation';

export * as Product from 'types/ShoppingCart/Product';

export type Type = {
	id: string;
	ingredient: Ingredient.Type;
	store: string;
	quantity: number;
	isFluid: boolean;
	source: Source.Type;
	// We need this from the ERP-Service
	size: number;
	imageUrl: string;
	price: number;
	weight: number;
	gtin: string;
	nutrients: { [nutrientId: string]: number | null };
};

export function create(): Type {
	return {
		id: '',
		ingredient: Ingredient.create(),
		store: '',
		quantity: 0,
		isFluid: false,
		source: Source.create(),
		imageUrl: '',
		price: 0,
		size: 100,
		weight: 0,
		gtin: '',
		nutrients: {},
	};
}

export function getUniqId(obj: Type) {
	return obj.id + obj.store;
}

export function mapToReceiptData(obj: Type, cultureCode: string): ReceiptData {
	return {
		id: obj.id,
		name: Translation.getFirstTranslation(obj.ingredient.name, cultureCode),
		store: obj.store,
		gtin: obj.gtin,
		quantity: getQuantity(obj) ?? 0,
		price: obj.price,
	};
}

export function setQuantity(obj: Type, quantity: number): Type {
	return { ...obj, quantity: quantity };
}

export function getNetQuantity(obj: Pick<Type, 'quantity'>): number {
	return Math.ceil(obj.quantity);
}

export function getQuantity(obj: Pick<Type, 'quantity' | 'weight' | 'size'>): number | null {
	if (!obj.size) return null;
	return Math.ceil(obj.weight / obj.size);
}

export function getPrice(obj: Pick<Type, 'quantity' | 'price'>): number {
	return getNetQuantity(obj) * obj.price;
}

export function mapFromApi(input: apiGetDetails.ProductItems): Type {
	return {
		id: input.productId,
		ingredient: Ingredient.mapFromApi(input),
		store: input.store,
		weight: input.weightOrVolume,
		quantity: 1,
		isFluid: input.isFluid,
		source: Source.mapFromApi(input),
		// We need this from the ERP-Service
		size: 100,
		imageUrl: '',
		price: 0,
		gtin: '',
		nutrients: {},
	};
}

export function getFullWeight(obj: Type): number {
	const quantity: number | null = getQuantity(obj);
	if (!quantity) return 0;
	return quantity * obj.weight;
}

export function getFullPrice(obj: Type): number {
	const quantity: number | null = getQuantity(obj);
	if (!quantity) return 0;
	return quantity * obj.price;
}

export function getUnit(obj: Type): string {
	if (obj.isFluid) return 'ml';
	return 'g';
}

export function renderWeight(value: number, product: Type, concatUnit: boolean): string {
	function isInteger(number: number): boolean {
		return number % 1 === 0;
	}
	if (value < 1000) {
		let formattedValue;
		if (isInteger(value)) {
			formattedValue = value.toFixed(0);
		} else {
			formattedValue = value.toFixed(value < 1 ? 2 : value < 10 ? 1 : 0);
		}
		const finalValue = formattedValue.endsWith('.0')
			? formattedValue.slice(0, -2)
			: formattedValue;
		if (concatUnit) return finalValue + getUnit(product);
		return finalValue;
	}
	const unitLabel = product.isFluid ? 'l' : 'kg';
	const adjustedValue = value / 1000;
	const formattedAdjustedValue = isInteger(adjustedValue)
		? adjustedValue.toFixed(0)
		: adjustedValue.toFixed(1);

	const finalValue = formattedAdjustedValue.endsWith('.0')
		? formattedAdjustedValue.slice(0, -2)
		: formattedAdjustedValue;

	if (concatUnit) return finalValue + unitLabel;
	return finalValue;
}
